import { generatePath, Link, useNavigate } from "react-router-dom";
import EntityTypes from "../../models/entityTypes";
import { api, SearchProps } from "../../store/api";
import RecordsListScreen from "../recordsListScreen";
import { ResourceDetailsRoute } from "./resourceDetailsScreen";
import useLabel from "../../util/useLabel";
import { useEffect, useState } from "react";
import { Panel, PanelBody, PanelFooter, PanelHeader } from "../../components/panel/panel";
import Resource from "../../models/resource";
import Paginated from "../../models/paginated";
import { format } from "../../helpers/format";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchAttributes, selectConfig } from "../../store/configSlice";
import AttributeTypes from "../../models/attributeTypes";
import AttributeValue from "../../models/attributeValue";
import Attribute from "../../models/attribute";
import { AttributeValueSummary } from "../../components/attribute/attributeValueSummary";

export const ResourcesRoute = "/resources";

export default function ResourcesSearchScreen() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const label = useLabel(EntityTypes.Resource);
  const config = useAppSelector(selectConfig);
  const attributes = config.attributes[EntityTypes.Resource];
  useEffect(() => { dispatch(fetchAttributes(EntityTypes.Resource)) }, [dispatch]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<String>();

  const [name, setName] = useState("");
  const [author, setAuthor] = useState("");

  const [paginated, setPaginated] = useState<Paginated<Resource>>();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState('');
  const [sortDescending, setsortDescending] = useState(false);

  const [attributeValues, setAttributeValues] = useState<AttributeValue[]>([]);

  const onAttributeValueString = (attribute: Attribute, value: string) => {
    console.log('onAttributeValueString', attribute, value);

    setAttributeValues((cur) => attributes?.map((thisAttribute) => {
      if (thisAttribute.id === attribute.id) {
        console.log('onAttributeValueString UPDATE', attribute, value);
        return {
          id: thisAttribute.id,
          stringValue: value,
        };
      }
      return cur.find((existing) => existing.id === thisAttribute.id) ?? {
        id: thisAttribute.id,
      };
    }) ?? []);
  };

  const onAttributeValueChoice = (attribute: Attribute, value: string) => {
    console.log('onAttributeValueChoice', attribute, value);

    setAttributeValues((cur) => attributes?.map((thisAttribute) => {
      if (thisAttribute.id === attribute.id) {
        console.log('onAttributeValueChoice UPDATE', attribute, value);
        const choiceId = parseInt(value);
        return {
          id: thisAttribute.id,
          choicesValue: isNaN(choiceId) ? [] : [choiceId],
        };
      }
      return cur.find((existing) => existing.id === thisAttribute.id) ?? {
        id: thisAttribute.id,
      };
    }) ?? []);
  };

  const onSearch = () => {
    setIsLoading(true);
    setPaginated(undefined);
    const searchProps = {
      name,
      attributeValues,
    };
    api.resources.searchAdvanced(searchProps)
      .then((records) => {
        setError(undefined);
        setPaginated(records);
        setIsLoading(false);
      })
      .catch((e: Error) => {
        console.error(e);
        setError("Unable to load " + label?.plural);
        setIsLoading(false);
      });
  };

  const endRecordNumber = ((page - 1) * (paginated?.perPage ?? 0)) + (paginated?.records.length ?? 0);
  const totalRecords = paginated?.recordsTotal ?? 0;

  const attributeColumns = attributes?.filter((attrib) =>
    attrib.isShownDefault
    || attributeValues.some((av) => av.id === attrib.id && ((av.stringValue ?? false) || (av.choicesValue ?? []).length > 0))
  );

  return <div>
    <div className="d-flex align-items-center mb-3">
      <div>
        <ul className="breadcrumb">
          <li className="breadcrumb-item"><a href="#/">{label?.plural}</a></li>
          <li className="breadcrumb-item active">Search</li>
        </ul>
        <h1 className="page-header mb-0">{label?.plural}</h1>
      </div>
    </div>

    <Panel className="card border-0" isLoading={isLoading}>
      <PanelHeader>Search Filters</PanelHeader>
      <PanelBody className="card-body">
        {error && <div className="alert alert-danger"><strong>Error!</strong> {error}</div>}

        <div className="row g-3">
          <div className="col-sm-6">
            <label htmlFor="name" className="form-label">Title/Name</label>
            <input type="text" className="form-control" id="name" placeholder="" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          </div>
          {attributes?.map((attrib) => {
            const attributeValue = attributeValues.find((av) => av.id === attrib.id);
            return <div className="col-sm-6" key={'attribute_' + attrib.id}>
              <label htmlFor={"attrib" + attrib.id} className="form-label">{attrib.name}</label>
              {attrib.type === AttributeTypes.String && <input type="text" className="form-control" id={"attrib" + attrib.id} placeholder="" value={attributeValue?.stringValue ?? ''} onChange={(e) => onAttributeValueString(attrib, e.currentTarget.value)} />}
              {attrib.type === AttributeTypes.Choice && <select className="form-control" id={"attrib" + attrib.id} placeholder="" onChange={(e) => onAttributeValueChoice(attrib, e.currentTarget.value)} value={attributeValue?.choicesValue?.map(choiceValue => choiceValue.toString()) ?? ''}>
                <option></option>
                {attrib.choices
                  ?.filter((attribChoice) => attribChoice.isActive)
                  .filter((attribChoice) =>
                    // not dependant on anoother attribute
                    (attribChoice.dependantOnAttributeChoiceIds ?? []).length === 0
                    // check whether dependant choice is selected
                    || attribChoice.dependantOnAttributeChoiceIds!.some((dependantChoiceId) => attributeValues.some((otherAttribValue) => otherAttribValue.choicesValue?.includes(dependantChoiceId) ?? false))
                  )
                  .map((attribChoice) => <option key={attribChoice.id} value={attribChoice.id}>{attribChoice.label}</option>)}
              </select>}
            </div>;
          })}
        </div>

      </PanelBody>
      <PanelFooter>
        <button type="button" className="btn btn-success" onClick={onSearch}>Search</button>
      </PanelFooter>
    </Panel>

    {(paginated?.recordsTotal ?? 0) > 0 && <Panel>
      <PanelBody>
        {(paginated?.recordsTotal ?? 0) === 0 && <p className="text-center">
          There are no {label?.plural} that match your search/filter.
        </p>}

        <div className="overflow-x-auto"><table className="table table-hover table-panel text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th>Title/Name</th>
              {attributeColumns?.map((attrib) => <th>{attrib.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {paginated?.records.map(record => <tr key={`${record.id}`}>
              <td><Link to={generatePath(ResourceDetailsRoute, { id: `${record.id}` })}>{record.name}</Link> {record?.isActive === false && <span className="badge bg-danger">Inactive</span>}</td>
              {attributeColumns?.map((attrib) => {
                return <td key={attrib.id}>
                  <AttributeValueSummary attribute={attrib} attributeValues={record.attributes} />
                </td>;
              })}
            </tr>)}
          </tbody>
        </table>
        </div>
        <div className="mt-1 d-flex flex-row align-items-center justify-content-around">
          <div>
            {format.number(1 + ((page - 1) * (paginated?.perPage ?? 0)))} &ndash; {format.number(endRecordNumber)} of {format.number(paginated?.recordsTotal ?? 0)} total
          </div>
          <div className="d-flex flex-row align-items-center">
            <div className="input-group input-group-sm">
              <span className="input-group-text" id="inputGroup-sizing-sm">Page</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((page) => page - 1)}><i className="fa fa-angle-left"></i></button>
              <input type="text" min={1} step={1} className="form-control text-center w-50px" value={page} onChange={(e) => {
                const newPage = parseInt(e.currentTarget.value);
                if (newPage) {
                  setPage(newPage);
                }
              }} />
              <button className="btn btn-outline-secondary btn-sm" disabled={endRecordNumber >= totalRecords} onClick={() => setPage((page) => page + 1)}><i className="fa fa-angle-right"></i></button>
            </div>
          </div>
          <div className="d-flex flex-row align-items-center">
            <div className="input-group input-group-sm">
              <span className="input-group-text" id="inputGroup-sizing-sm">Rows per page:</span>
              <select value={perPage} className="form-control form-control-sm" onChange={(e) => setPerPage(parseInt(e.currentTarget.value))}><option>10</option><option>50</option><option>100</option></select>
            </div>
          </div>
        </div>
      </PanelBody>
    </Panel>}

  </div >;
}